import * as React from "react";
import * as _ from "lodash";
import printJs from "print-js";
import { IPrescription, IAppointmentScheduleResponse } from "@curefit/care-common";
import {default as AppUtils, LOADING_STATE} from "../../utils/AppUtils";
import {AppConstants, AppMessages, ConsultationStatusConstant} from "../../utils/AppConstants";
import {IOngoingConsultation} from "../../models/IOngoingConsultation";
import IPatientCompleteInfo from "../../models/patients/IPatientCompleteInfo";
import SocketIOUtils from "../../utils/SocketIOUtils";
import LocalStorageUtils from "../../utils/LocalStorageUtils";
import {MODAL_SCREENS} from "./OngoingConsultationContainer";
import RollbarUtils from "../../utils/RollbarUtils";
import IDoctorProfile from "../../models/IDoctorProfile";
import {ActionResult} from "../../../../common/models/IGenericWorkflowActionResult";
import {ISavePlanResponse} from "../../../../common/models/ISavePlanResponse";
import IPatientLifestylePlanWrapper from "../../models/patients/IPatientLifestylePlanWrapper";
import { ORION_IMG_URL } from "../../utils/AppConstants";
import {EventNames} from "../../utils/EventTrackerConstants";

export interface PdfSaveFailureModalProps {
    doctorInfo: IDoctorProfile,
    bookingId: number,
    consultationId: number,
    patientDetails: IPatientCompleteInfo,
    ongoingConsultation: IOngoingConsultation,
    toggleModal: (flag: boolean, clearPdfData?: boolean, modalScreen?: string) => void,
    showDifferentialDiagnosisModal: () => void,
    pdfData: File,
    pdfPreviewData: File,
    prescriptionSaveError: string,
    savePrescription: (appointmentId: number, prescription: IPrescription) => Promise<IPrescription>,
    uploadPrescription: (appointmentId: number, prescription: File, type: string) => Promise<any>,
    uploadPlan: (appointmentId: number, plan: File, type: string) => Promise<any>,
    updateAppointmentStatus: (bookingId: number, appointmentId: number, status: ConsultationStatusConstant) => Promise<any>,
    onPrescriptionSaveSuccess: (pdfData: File, pdfPreviewData: File) => void,
    isPdfDataSaved: boolean,
    savePrescriptionNew: (consultation: IAppointmentScheduleResponse, prescription: IPrescription, patientPlan: IPatientLifestylePlanWrapper, planId: string) => Promise<any>,
    removeWorkflowObject: (consultation: IAppointmentScheduleResponse, isPrescriptionObject: boolean) => Promise<any>,
    planSaveError: string,
    onPlanSaveSuccess: (pdfData: File, pdfPreviewData: File) => void,
    errorMessage: string,
    saveUserPlan: (consultation: IAppointmentScheduleResponse, prescription: IPrescription, patientPlan: IPatientLifestylePlanWrapper, planId: string) => Promise<any>,
    isOnlyErrorModal: boolean
}

export interface PdfSaveFailureModalState {
    showErrorDetails: boolean,
    pdfSaveStatus: LOADING_STATE,
    prescriptionSaveError: string,
    isPdfDataSaved: boolean,
    rollbarLoggingStatus: LOADING_STATE,
    rollbarLoggingSuccess: boolean
}

class PdfSaveFailureModal extends React.Component<PdfSaveFailureModalProps, PdfSaveFailureModalState> {
    constructor(props: PdfSaveFailureModalProps) {
        super(props);
        let prescriptionSaveError = props.prescriptionSaveError;
        let isPdfDataSaved = props.isPdfDataSaved;
        this.state = {
            showErrorDetails: false,
            pdfSaveStatus: LOADING_STATE.NOT_INITIALIZED,
            prescriptionSaveError,
            isPdfDataSaved,
            rollbarLoggingStatus: LOADING_STATE.NOT_INITIALIZED,
            rollbarLoggingSuccess: false
        }
    }

    preventModalClose = (event: any) => {
        event.stopPropagation();
    }

    savePrescriptionPDF = () => {
        this.setState({
            pdfSaveStatus: LOADING_STATE.LOADING
        }, () => {
            this.props.uploadPrescription(this.props.consultationId, this.props.pdfData, AppConstants.pdfAttachmentType.EMAIL_ATTACHMENT).then(() => {
                return this.props.uploadPrescription(this.props.consultationId, this.props.pdfPreviewData, AppConstants.pdfAttachmentType.PRINT_ATTACHMENT)
            }).then(() => {
                return this.props.removeWorkflowObject(this.props.ongoingConsultation.appointmentSchedule, true)
            }).then(() => {
                return this.props.updateAppointmentStatus(this.props.bookingId, this.props.consultationId, ConsultationStatusConstant.COMPLETED)
            }).then(() => {
                return this.props.onPrescriptionSaveSuccess(this.props.pdfData, this.props.pdfPreviewData)
            }).catch((err: any) => {
                console.log(err);
                this.setState({
                    prescriptionSaveError: AppMessages.PRESCRIPTION_PDF_SAVE_FAILURE,
                    pdfSaveStatus: LOADING_STATE.LOADED
                })
            })
        })
    }

    savePrescription = () => {
        let prescription = this.props.ongoingConsultation.currentPrescription;
        this.setState({
            pdfSaveStatus: LOADING_STATE.LOADING
        }, () => {
            this.props.savePrescriptionNew(this.props.ongoingConsultation.appointmentSchedule, prescription, null, null).then((saveResponse: ISavePlanResponse) => {
                if (saveResponse.status === ActionResult.SUCCESS) {
                    SocketIOUtils.deletePrescription(this.props.consultationId);
                    LocalStorageUtils.clearPrescription(this.props.consultationId);
                    this.savePrescriptionPDF();
                } else {
                    const errorMsg = AppUtils.getWorkflowFailureError(saveResponse.workflowStates);
                    this.setState({
                        prescriptionSaveError: errorMsg || AppMessages.SAVE_PRESCRIPTION_UNSUCCESSFUL,
                        pdfSaveStatus: LOADING_STATE.LOADED
                    })
                }
            }).catch((error: any) => {
                if (!_.isEmpty(error)) {
                    const errorMsg = AppUtils.getWorkflowFailureError(error.workflowStates);
                    this.setState({
                        prescriptionSaveError: errorMsg,
                        pdfSaveStatus: LOADING_STATE.LOADED
                    })
                } else {
                    this.setState({
                        prescriptionSaveError: null,
                        pdfSaveStatus: LOADING_STATE.LOADED
                    })
                }
            });
        })
    }

    savePlanPDF = () => {
        this.setState({
            pdfSaveStatus: LOADING_STATE.LOADING
        }, () => {
            this.props.uploadPlan(this.props.consultationId, this.props.pdfData, AppConstants.planPdfAttachmentType.EMAIL_ATTACHMENT).then(() => {
                return this.props.uploadPlan(this.props.consultationId, this.props.pdfPreviewData, AppConstants.planPdfAttachmentType.PRINT_ATTACHMENT)
            }).then(() => {
                return this.props.removeWorkflowObject(this.props.ongoingConsultation.appointmentSchedule, false)
            }).then(() => {
                return this.props.updateAppointmentStatus(this.props.bookingId, this.props.consultationId, ConsultationStatusConstant.COMPLETED)
            }).then(() => {
                return this.props.onPlanSaveSuccess(this.props.pdfData, this.props.pdfPreviewData)
            }).catch((err) => {
                this.setState({
                    prescriptionSaveError: AppMessages.PRESCRIPTION_PDF_SAVE_FAILURE,
                    pdfSaveStatus: LOADING_STATE.LOADED
                })
            })
        })
    }

    saveUserPlan = () => {
        let { ongoingConsultation } = this.props;
        let consultation = ongoingConsultation.appointmentSchedule;
        let patientPlan = ongoingConsultation.patientPlan;
        let planId = !_.isEmpty(ongoingConsultation.planMeta) ? ongoingConsultation.planMeta._id : null;
        this.setState({
            pdfSaveStatus: LOADING_STATE.LOADING
        }, () => {
            this.props.saveUserPlan(consultation, null, patientPlan, planId).then((planSaveResponse: ISavePlanResponse) => {
                if (planSaveResponse.status === ActionResult.SUCCESS) {
                    this.savePlanPDF();
                }
                else {
                    const errorMsg = AppUtils.getWorkflowFailureError(planSaveResponse.workflowStates);
                    this.setState({
                        prescriptionSaveError: errorMsg,
                        pdfSaveStatus: LOADING_STATE.LOADED
                    })
                }
            }).catch((error: any) => {
                if (!_.isEmpty(error)) {
                    const errorMsg = AppUtils.getWorkflowFailureError(error.workflowStates);
                    this.setState({
                        prescriptionSaveError: errorMsg,
                        pdfSaveStatus: LOADING_STATE.LOADED
                    })
                } else {
                    this.setState({
                        prescriptionSaveError: null,
                        pdfSaveStatus: LOADING_STATE.LOADED
                    })
                }
            });
        })
    }

    savePrescriptionAndOrPDF = () => {
        LocalStorageUtils.setClickEvents({ field: "Try Again", section: EventNames.Complete_Prescription + "Failed", widget: EventNames.Print_Prescription })
        if (this.state.pdfSaveStatus !== LOADING_STATE.LOADING) {
            this.setState({
                rollbarLoggingStatus: LOADING_STATE.NOT_INITIALIZED,
                rollbarLoggingSuccess: false
            }, () => {
                if (this.props.ongoingConsultation && this.props.ongoingConsultation.appointmentSchedule && this.props.ongoingConsultation.appointmentSchedule.consultationProduct.isPlan) {
                    if (this.state.isPdfDataSaved) this.savePlanPDF();
                    else this.saveUserPlan();
                } else {
                    if (this.props.isPdfDataSaved) this.savePrescriptionPDF();
                    else this.savePrescription();
                }
            })
        }
    }

    showErrorDetails = () => {
        LocalStorageUtils.setClickEvents({ field: "View Details", section: EventNames.Complete_Prescription + "Failed", widget: EventNames.Print_Prescription })
        this.setState({
            showErrorDetails: true
        })
    }

    rollbarCallback = (err: any, data: any) => {
        if (!_.isNull(err)) {
            this.setState({
                rollbarLoggingStatus: LOADING_STATE.LOADED,
                rollbarLoggingSuccess: false
            })
        } else {
            this.setState({
                rollbarLoggingStatus: LOADING_STATE.LOADED,
                rollbarLoggingSuccess: true
            })
        }
    }

    reportError = () => {
        if (!this.state.rollbarLoggingSuccess) {
            let details = this.props.isPdfDataSaved ? "Error in uploading prescription PDF" : "Error in saving prescription";
            let request = this.props.isPdfDataSaved ? this.props.pdfData : this.props.ongoingConsultation.currentPrescription;
            let rollbarPayload = { message: this.state.prescriptionSaveError, details, consultationId: this.props.consultationId, reporterName: this.props.doctorInfo.name, reporterId: this.props.doctorInfo.id, request }
            let error = new Error(this.state.prescriptionSaveError);
            this.setState({
                rollbarLoggingStatus: LOADING_STATE.LOADING
            }, () => {
                RollbarUtils.sendErrorToRollbar(error, rollbarPayload, this.rollbarCallback)
            })
        }
    };

    handleContinueEditing = () => {
        LocalStorageUtils.setClickEvents({ field: "Continue Editing", section: EventNames.Complete_Prescription + "Failed", widget: EventNames.Print_Prescription })
        this.props.toggleModal(false, true, MODAL_SCREENS[1]);
    };

    handlePrintPdf = () => {
        LocalStorageUtils.setClickEvents({ field: "Print", section: EventNames.Complete_Prescription + "Failed", widget: EventNames.Print_Prescription })
        printJs({printable: URL.createObjectURL(this.props.pdfPreviewData), type: "pdf"});
    };

    render() {
        let myInstance = this;
        let pdfPrescription = null;
        if (this.props.ongoingConsultation && this.props.ongoingConsultation.appointmentSchedule) {
            pdfPrescription = {...this.props.ongoingConsultation.appointmentSchedule};
            pdfPrescription.prescription = this.props.ongoingConsultation.currentPrescription ? {...this.props.ongoingConsultation.currentPrescription} : null;
        }
        let saveFailedLabelText = this.props.isPdfDataSaved ? AppMessages.PRESCRIPTION_PDF_SAVE_FAILURE : AppMessages.SAVE_PRESCRIPTION_UNSUCCESSFUL;
        let tryAgainText = this.state.pdfSaveStatus === LOADING_STATE.LOADING ? "Saving..." : "Try again";
        let errorDetailsCssClass = this.state.showErrorDetails ? "" : "hidden";
        // let reportButtonCssClass = this.state.rollbarLoggingSuccess ? "success" : "";
        // let reportButtonText = this.state.rollbarLoggingSuccess ? "Reported" : "Report";
        return (
            <div className={"prescription-successful-save-container"} onClick={this.preventModalClose}>
                {/*<div className={`log-error-button ${reportButtonCssClass}`} onClick={this.reportError}>*/}
                    {/*{*/}
                        {/*this.state.rollbarLoggingStatus === LOADING_STATE.LOADING ?*/}
                            {/*<img className={"loader"} src={ORION_IMG_URL + "loader/rolling-loader.gif"} /> :*/}
                            {/*<span>*/}
                                {/*{reportButtonText}*/}
                                {/*{ this.state.rollbarLoggingSuccess && <span className={"glyphicon glyphicon-ok"} /> }*/}
                            {/*</span>*/}
                    {/*}*/}
                {/*</div>*/}
                <div className="patient-image-container">
                    <img className="patient-image" src={ORION_IMG_URL + "default-user-image.png"} />
                </div>
                <div className="patient-info-container">
                    <div>{myInstance.props.patientDetails.profile.name}, {myInstance.props.patientDetails.profile.age}</div>
                </div>
                {
                    this.state.pdfSaveStatus === LOADING_STATE.LOADING ?
                        <img src={ORION_IMG_URL + "loader/rolling-loader.gif"} width="50px" /> :
                        <div>
                            <div className="prescription-save-unsuccessful-label">
                                {saveFailedLabelText}
                                <span className={this.state.showErrorDetails ? "view-details hidden" : "view-details"} onClick={this.showErrorDetails}>View Details</span>
                            </div>
                            {
                                !_.isEmpty(this.state.prescriptionSaveError) &&
                                <div className={"prescription-save-unsuccessful-label-details " + errorDetailsCssClass}>
                                    <span>{this.state.prescriptionSaveError}</span>
                                </div>
                            }
                        </div>
                }
                <div className={"footer"}>
                    <div className="save-button collapsible-card button-container">
                        <input className="card-primary-action continue-editing" type="button" value="Continue Editing" onClick={this.handleContinueEditing} />
                        <input className="card-primary-action send-prescription" type="button" value={tryAgainText} onClick={() => this.savePrescriptionAndOrPDF()} />
                        <input className="card-primary-action" type="button" value="Print" onClick={this.handlePrintPdf} />
                        <div style={{ marginTop: "20px" }}>
                            <input className="card-primary-action continue-editing proceed-anyways" type="button" value="Proceed anyways" style={{width: "auto", border: "none"}}
                               onClick={() => {
                                LocalStorageUtils.setClickEvents({ field: "Proceed Anyways", section: EventNames.Complete_Prescription + "Failed", widget: EventNames.Print_Prescription })
                                this.props.showDifferentialDiagnosisModal()
                            }} />
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

export default PdfSaveFailureModal;